import React, { useEffect, useState, Fragment } from 'react';
import { ArrowLeft, ArrowRight, Check, ChevronRight, Clock, Edit2, Eye, Globe, Image as ImageIcon, Lock, Plus, Trash2, Users, X, Calendar, Trophy, BarChart2, MessageCircle, Heart, Sparkles } from 'lucide-react';
type PollOption = {
  id: string;
  text: string;
  image?: string;
};
type PollVisibility = 'public' | 'followers' | 'private';
type PollDuration = '1h' | '6h' | '12h' | '24h' | '3d' | '7d';
interface PollGeneratorProps {
  onClose: () => void;
  onPollCreated?: (pollData: any) => void;
  ritualId?: string;
  initialData?: {
    question: string;
    options: PollOption[];
    visibility: PollVisibility;
    duration: PollDuration;
    allowComments: boolean;
    allowImageOptions: boolean;
  };
}
export const PollGenerator: React.FC<PollGeneratorProps> = ({
  onClose,
  onPollCreated,
  ritualId,
  initialData
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [question, setQuestion] = useState(initialData?.question || '');
  const [options, setOptions] = useState<PollOption[]>(initialData?.options || [{
    id: '1',
    text: ''
  }, {
    id: '2',
    text: ''
  }]);
  const [visibility, setVisibility] = useState<PollVisibility>(initialData?.visibility || 'public');
  const [duration, setDuration] = useState<PollDuration>(initialData?.duration || '24h');
  const [allowComments, setAllowComments] = useState(initialData?.allowComments !== undefined ? initialData.allowComments : true);
  const [allowImageOptions, setAllowImageOptions] = useState(initialData?.allowImageOptions !== undefined ? initialData.allowImageOptions : false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenReward, setTokenReward] = useState(15); // Default token reward for completing the ritual
  // Reset error when step changes
  useEffect(() => {
    setError(null);
  }, [currentStep]);
  const steps = [{
    id: 'question',
    title: 'Question'
  }, {
    id: 'options',
    title: 'Options'
  }, {
    id: 'settings',
    title: 'Settings'
  }, {
    id: 'preview',
    title: 'Preview'
  }];
  const addOption = () => {
    if (options.length < 10) {
      setOptions([...options, {
        id: (options.length + 1).toString(),
        text: ''
      }]);
    }
  };
  const removeOption = (id: string) => {
    if (options.length > 2) {
      setOptions(options.filter(option => option.id !== id));
    }
  };
  const updateOption = (id: string, text: string) => {
    setOptions(options.map(option => option.id === id ? {
      ...option,
      text
    } : option));
  };
  const handleNext = () => {
    if (currentStep === 0) {
      if (!question.trim()) {
        setError('Please enter a question');
        return;
      }
    } else if (currentStep === 1) {
      const emptyOptions = options.filter(option => !option.text.trim());
      if (emptyOptions.length > 0) {
        setError('Please fill in all options');
        return;
      }
      if (options.length < 2) {
        setError('Please add at least 2 options');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Create poll data object
      const pollData = {
        question,
        options,
        visibility,
        duration,
        allowComments,
        allowImageOptions,
        createdAt: new Date().toISOString(),
        ritualId,
        votes: {},
        totalVotes: 0
      };
      // Here you would typically send this data to your backend
      console.log('Creating poll:', pollData);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Call the onPollCreated callback with the poll data
      if (onPollCreated) {
        onPollCreated(pollData);
      }
      // Dispatch event to notify other components about the poll creation
      const event = new CustomEvent('pollCreated', {
        detail: {
          pollData,
          ritualId
        }
      });
      document.dispatchEvent(event);
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error creating poll:', error);
      setError('Failed to create poll. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderStepIndicator = () => {
    return <div className="flex items-center justify-center mb-6" data-id="element-15594">
        {steps.map((step, index) => <Fragment key={step.id} data-id="element-15595">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${index === currentStep ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : index < currentStep ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-15596">
              {index < currentStep ? <Check size={16} data-id="element-15597" /> : <span className="text-sm font-medium" data-id="element-15598">{index + 1}</span>}
            </div>
            {index < steps.length - 1 && <div className={`w-12 h-1 ${index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-15599"></div>}
          </Fragment>)}
      </div>;
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <div className="space-y-4" data-id="element-15600">
            <div data-id="element-15601">
              <label htmlFor="question" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-15602">
                What would you like to ask?
              </label>
              <textarea id="question" value={question} onChange={e => setQuestion(e.target.value)} placeholder="Enter your question here..." className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-pink-500 focus:border-transparent" rows={3} maxLength={200} data-id="element-15603" />
              <div className="flex justify-end mt-1" data-id="element-15604">
                <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-15605">
                  {question.length}/200
                </span>
              </div>
            </div>
            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3 border border-pink-100 dark:border-pink-800/30" data-id="element-15606">
              <div className="flex items-start gap-3" data-id="element-15607">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-pink-100 dark:bg-pink-800/30 flex items-center justify-center text-pink-500" data-id="element-15608">
                  <Trophy size={16} data-id="element-15609" />
                </div>
                <div data-id="element-15610">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-15611">
                    Ritual Challenge
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1" data-id="element-15612">
                    Creating this poll will help complete your ritual challenge.
                    You'll earn {tokenReward} tokens when people vote on your
                    poll.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 1:
        return <div className="space-y-4" data-id="element-15613">
            <div className="flex items-center justify-between" data-id="element-15614">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-15615">
                Poll Options
              </h3>
              <div className="flex items-center" data-id="element-15616">
                <label className="text-xs text-gray-500 dark:text-gray-400 mr-2" data-id="element-15617">
                  Allow images
                </label>
                <button type="button" className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${allowImageOptions ? 'bg-pink-500' : 'bg-gray-200 dark:bg-gray-700'}`} onClick={() => setAllowImageOptions(!allowImageOptions)} data-id="element-15618">
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${allowImageOptions ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-15619" />
                </button>
              </div>
            </div>
            <div className="space-y-3 max-h-60 overflow-y-auto pr-1" data-id="element-15620">
              {options.map((option, index) => <div key={option.id} className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2" data-id="element-15621">
                  <div className="w-6 h-6 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-15622">
                    {index + 1}
                  </div>
                  <input type="text" value={option.text} onChange={e => updateOption(option.id, e.target.value)} placeholder={`Option ${index + 1}`} className="flex-1 p-2 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white" data-id="element-15623" />
                  {allowImageOptions && <button type="button" className="p-1.5 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300" data-id="element-15624">
                      <ImageIcon size={16} data-id="element-15625" />
                    </button>}
                  <button type="button" onClick={() => removeOption(option.id)} disabled={options.length <= 2} className={`p-1.5 ${options.length <= 2 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'}`} data-id="element-15626">
                    <Trash2 size={16} data-id="element-15627" />
                  </button>
                </div>)}
            </div>
            {options.length < 10 && <button type="button" onClick={addOption} className="w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-15628">
                <Plus size={16} data-id="element-15629" />
                <span data-id="element-15630">Add Option</span>
              </button>}
            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-15631">
              You can add up to 10 options
            </div>
          </div>;
      case 2:
        return <div className="space-y-4" data-id="element-15632">
            <div data-id="element-15633">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-15634">
                Poll Duration
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-15635">
                {[{
                value: '1h',
                label: '1 hour'
              }, {
                value: '6h',
                label: '6 hours'
              }, {
                value: '12h',
                label: '12 hours'
              }, {
                value: '24h',
                label: '1 day'
              }, {
                value: '3d',
                label: '3 days'
              }, {
                value: '7d',
                label: '1 week'
              }].map(option => <button key={option.value} type="button" className={`py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1 ${duration === option.value ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} onClick={() => setDuration(option.value as PollDuration)} data-id="element-15636">
                    <Clock size={14} data-id="element-15637" />
                    <span data-id="element-15638">{option.label}</span>
                  </button>)}
              </div>
            </div>
            <div data-id="element-15639">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-15640">
                Visibility
              </label>
              <div className="space-y-2" data-id="element-15641">
                {[{
                value: 'public',
                label: 'Public',
                description: 'Anyone can see and vote on this poll',
                icon: <Globe size={16} data-id="element-15642" />
              }, {
                value: 'followers',
                label: 'Followers Only',
                description: 'Only your followers can see and vote',
                icon: <Users size={16} data-id="element-15643" />
              }, {
                value: 'private',
                label: 'Private',
                description: 'Only people with the link can vote',
                icon: <Lock size={16} data-id="element-15644" />
              }].map(option => <button key={option.value} type="button" className={`w-full p-3 rounded-lg flex items-start gap-3 text-left ${visibility === option.value ? 'bg-pink-50 dark:bg-pink-900/20 border border-pink-100 dark:border-pink-800/30' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`} onClick={() => setVisibility(option.value as PollVisibility)} data-id="element-15645">
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${visibility === option.value ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`} data-id="element-15646">
                      {option.icon}
                    </div>
                    <div data-id="element-15647">
                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-15648">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-15649">
                        {option.description}
                      </div>
                    </div>
                  </button>)}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-id="element-15650">
              <div className="flex items-center gap-2" data-id="element-15651">
                <MessageCircle size={16} className="text-gray-400" data-id="element-15652" />
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-15653">
                  Allow comments
                </span>
              </div>
              <button type="button" className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${allowComments ? 'bg-pink-500' : 'bg-gray-200 dark:bg-gray-700'}`} onClick={() => setAllowComments(!allowComments)} data-id="element-15654">
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${allowComments ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-15655" />
              </button>
            </div>
          </div>;
      case 3:
        return <div className="space-y-4" data-id="element-15656">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-15657">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-15658">
                <div className="flex items-center gap-3" data-id="element-15659">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500" data-id="element-15660"></div>
                  <div data-id="element-15661">
                    <div className="font-medium text-gray-900 dark:text-white" data-id="element-15662">
                      Your Name
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-15663">
                      Just now
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-15664">
                <p className="text-gray-900 dark:text-white" data-id="element-15665">{question}</p>
                <div className="mt-4 space-y-2" data-id="element-15666">
                  {options.map((option, index) => <button key={option.id} className="w-full p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors" data-id="element-15667">
                      {option.text}
                    </button>)}
                </div>
                <div className="mt-4 flex items-center justify-between text-gray-500 dark:text-gray-400 text-sm" data-id="element-15668">
                  <div className="flex items-center gap-4" data-id="element-15669">
                    <div className="flex items-center gap-1" data-id="element-15670">
                      <BarChart2 size={16} data-id="element-15671" />
                      <span data-id="element-15672">0 votes</span>
                    </div>
                    {allowComments && <div className="flex items-center gap-1" data-id="element-15673">
                        <MessageCircle size={16} data-id="element-15674" />
                        <span data-id="element-15675">0 comments</span>
                      </div>}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-15676">
                    <Clock size={16} data-id="element-15677" />
                    <span data-id="element-15678">Ends in {duration}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-4 border border-pink-100 dark:border-pink-800/30" data-id="element-15679">
              <div className="flex items-start gap-3" data-id="element-15680">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-pink-100 dark:bg-pink-800/30 flex items-center justify-center text-pink-500" data-id="element-15681">
                  <Trophy size={20} data-id="element-15682" />
                </div>
                <div data-id="element-15683">
                  <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-15684">
                    Ritual Poll Reward
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1" data-id="element-15685">
                    You'll earn {tokenReward} tokens when this poll reaches 10
                    votes. Share it to get more engagement!
                  </p>
                  <div className="mt-3 flex items-center gap-2" data-id="element-15686">
                    <Heart size={16} className="text-pink-500" fill="currentColor" data-id="element-15687" />
                    <span className="text-pink-500 font-medium" data-id="element-15688">
                      +{tokenReward}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-15689">
      <div className="relative w-full max-w-md bg-gray-50 dark:bg-gray-900 rounded-xl shadow-2xl animate-in zoom-in-90 duration-300" data-id="element-15690">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-15691">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-15692">
            <Sparkles size={18} className="text-pink-500" data-id="element-15693" />
            {ritualId ? 'Ritual Poll Generator' : 'Create Poll'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-15694">
            <X size={18} data-id="element-15695" />
          </button>
        </div>
        <div className="p-6" data-id="element-15696">
          {renderStepIndicator()}
          <div className="mb-6" data-id="element-15697">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-15698">
              {steps[currentStep].title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-15699">
              {currentStep === 0 ? 'Start by creating an engaging question' : currentStep === 1 ? 'Add options for people to vote on' : currentStep === 2 ? 'Configure your poll settings' : 'Review your poll before publishing'}
            </p>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-lg text-sm text-red-600 dark:text-red-400" data-id="element-15700">
              {error}
            </div>}
          {renderStepContent()}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-15701">
          <button type="button" onClick={currentStep === 0 ? onClose : handleBack} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-15702">
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>
          <button type="button" onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext} disabled={isSubmitting} className={`px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 ${isSubmitting ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600'}`} data-id="element-15703">
            {isSubmitting ? <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-15704"></div>
                <span data-id="element-15705">Creating...</span>
              </> : currentStep === steps.length - 1 ? <>
                <span data-id="element-15706">Create Poll</span>
                <Sparkles size={16} data-id="element-15707" />
              </> : <>
                <span data-id="element-15708">Continue</span>
                <ArrowRight size={16} data-id="element-15709" />
              </>}
          </button>
        </div>
      </div>
    </div>;
};